import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AppWindow, Plus, Pencil, Trash2, Globe, Building2, Receipt } from 'lucide-react';
import { GenerateInvoicesDialog } from './generate-invoices-dialog';

const INTEGRATIONS_WITH_GENERATOR = new Set(['MyTowMate']);

interface Application {
  id: string;
  name: string;
  domain: string | null;
  description: string | null;
  businessProfileId: string | null;
  businessProfileName: string | null;
  isActive: boolean;
}

interface BusinessProfile {
  id: string;
  name: string;
}

const emptyForm = {
  name: '',
  domain: '',
  description: '',
  businessProfileId: '',
};

export function ApplicationsPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [generatingFor, setGeneratingFor] = useState<Application | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['finance-applications'],
    queryFn: async () => {
      const result = await api.getFinanceApplications();
      return (result.data || []) as Application[];
    },
  });

  const { data: profiles } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: async () => {
      const result = await api.getBusinessProfiles();
      return (result.data || []) as BusinessProfile[];
    },
  });

  const apps = data || [];

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createFinanceApplication(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-applications'] });
      closeDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateFinanceApplication(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-applications'] });
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteFinanceApplication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-applications'] });
    },
  });

  function openCreate() {
    setEditingApp(null);
    setForm(emptyForm);
    setDialogOpen(true);
  }

  function openEdit(app: Application) {
    setEditingApp(app);
    setForm({
      name: app.name,
      domain: app.domain || '',
      description: app.description || '',
      businessProfileId: app.businessProfileId || '',
    });
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingApp(null);
    setForm(emptyForm);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      name: form.name,
      domain: form.domain || null,
      description: form.description || null,
      businessProfileId: form.businessProfileId || null,
    };

    if (editingApp) {
      updateMutation.mutate({ id: editingApp.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Applications</h1>
          <p className="text-muted-foreground">
            Products and applications you manage and bill for
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Application
        </Button>
      </div>

      {apps.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AppWindow className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-1">No applications yet</h3>
            <p className="text-muted-foreground mb-4">
              Add applications you manage to associate with invoices
            </p>
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Application
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {apps.map((app) => (
            <Card key={app.id} className={!app.isActive ? 'opacity-60' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AppWindow className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">{app.name}</h3>
                  </div>
                  {!app.isActive && <Badge variant="secondary">Inactive</Badge>}
                </div>

                {app.domain && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <Globe className="h-4 w-4" />
                    {app.domain}
                  </div>
                )}

                {app.businessProfileName && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <Building2 className="h-4 w-4" />
                    {app.businessProfileName}
                  </div>
                )}

                {app.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {app.description}
                  </p>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  {INTEGRATIONS_WITH_GENERATOR.has(app.name) && app.isActive && (
                    <Button
                      size="sm"
                      onClick={() => setGeneratingFor(app)}
                    >
                      <Receipt className="h-3 w-3 mr-1" />
                      Generate Invoices
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openEdit(app)}>
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this application?')) {
                        deleteMutation.mutate(app.id);
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {generatingFor && (
        <GenerateInvoicesDialog
          open={!!generatingFor}
          onOpenChange={(o) => !o && setGeneratingFor(null)}
          application={{ id: generatingFor.id, name: generatingFor.name }}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingApp ? 'Edit Application' : 'New Application'}
            </DialogTitle>
            <DialogDescription>
              {editingApp
                ? 'Update application details'
                : 'Add a new application or product you manage'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="app-name">Application Name *</Label>
              <Input
                id="app-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., acme.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="domain">Domain</Label>
              <Input
                id="domain"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                placeholder="e.g., acme.com"
              />
            </div>

            <div>
              <Label htmlFor="bp-select">Default Business Profile</Label>
              <Select
                value={form.businessProfileId}
                onValueChange={(value) =>
                  setForm({ ...form, businessProfileId: value === 'none' ? '' : value })
                }
              >
                <SelectTrigger id="bp-select">
                  <SelectValue placeholder="Select a business profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(profiles || []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingApp ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
